import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { changePassword } from '../api/auth';
import PasswordInput from './PasswordInput';

export default function ChangePasswordModal({ open, onClose }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');

    const mutation = useMutation({
        mutationFn: changePassword,
        onSuccess: (data) => {
            // The server revoked every outstanding refresh token, this tab's
            // included, and handed back a replacement pair. Storing it is what
            // keeps the user signed in here instead of 401-ing on the next call.
            if (data?.access) localStorage.setItem('access_token', data.access);
            if (data?.refresh) localStorage.setItem('refresh_token', data.refresh);
            onClose();
        },
        onError: (err) => {
            const data = err?.response?.data;
            setError(
                data?.new_password?.[0] ||
                    data?.detail ||
                    'Something went wrong. Please try again.'
            );
        },
    });

    // Lock background scroll while the modal is open, and close on Escape.
    // Mirrors DeleteAccountModal so the two behave identically.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    // Reset fields whenever the modal is (re)opened.
    useEffect(() => {
        if (open) {
            setCurrent('');
            setNext('');
            setConfirm('');
            setError('');
        }
    }, [open]);

    if (!open) return null;

    const canSubmit =
        current.length > 0 && next.length > 0 && confirm.length > 0 && !mutation.isPending;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        // Only the match is checked here. Strength is the server's call so the
        // rules stay in one place -- signup, reset and this all use
        // AUTH_PASSWORD_VALIDATORS.
        if (next !== confirm) {
            setError('New passwords do not match.');
            return;
        }
        setError('');
        mutation.mutate({ oldPassword: current, newPassword: next });
    };

    return (
        <div className="delete-modal-overlay" role="presentation" onClick={onClose}>
            <div
                className="delete-modal-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="change-password-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="delete-modal-header">
                    <h2 className="delete-modal-title" id="change-password-title">
                        Change Password
                    </h2>
                    <button
                        type="button"
                        className="delete-modal-close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <form className="delete-modal-form" onSubmit={handleSubmit}>
                    {error && (
                        <p className="delete-modal-error" role="alert">
                            {error}
                        </p>
                    )}

                    <label className="delete-modal-label" htmlFor="current-password">
                        Current password
                    </label>
                    <PasswordInput
                        id="current-password"
                        className="delete-modal-input"
                        wrapperClassName="delete-modal-field"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        autoComplete="current-password"
                    />

                    <label className="delete-modal-label" htmlFor="new-password">
                        New password
                    </label>
                    <PasswordInput
                        id="new-password"
                        className="delete-modal-input"
                        wrapperClassName="delete-modal-field"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        autoComplete="new-password"
                    />

                    <label className="delete-modal-label" htmlFor="confirm-password">
                        Confirm new password
                    </label>
                    <PasswordInput
                        id="confirm-password"
                        className="delete-modal-input"
                        wrapperClassName="delete-modal-field"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                    />

                    <div className="delete-modal-actions">
                        <button
                            type="button"
                            className="delete-modal-cancel"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="delete-modal-confirm"
                            disabled={!canSubmit}
                        >
                            {mutation.isPending ? 'Changing…' : 'Change Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
